/**
 * Tenant Data Service
 * ─────────────────────────────────────────────────────────────────
 * Provides a single, consistent API for reading and writing business
 * data (inventory, clients, etc.) that works transparently for both:
 *
 *   • Admin accounts  → uses local IndexedDB (existing behaviour)
 *   • Staff accounts  → uses DynamoDB, scoped by tenantId so all
 *                        members of the same workspace see the same data
 *
 * Usage:
 *   const svc = getTenantDataService(currentUser, userProfile);
 *   const items = await svc.getAll('inventory');
 *   await svc.put('inventory', item);
 *   await svc.remove('inventory', item.id);
 */

import {
  DynamoDBDocumentClient,
  ScanCommand,
  PutCommand,
  DeleteCommand,
} from '@aws-sdk/lib-dynamodb';
import { getTrackedDynamoDBClient } from './aws-core';
import * as localDB from '../utils/indexedDBService';

// ─── DynamoDB table that stores all shared tenant business data ───────────────
const TENANT_TABLE = 'akmo-books-tenant-data';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Build a composite DynamoDB PK:  "<tenantId>#<collection>" */
function pk(tenantId: string, collection: string) {
  return `${tenantId}#${collection}`;
}

// ─── Cloud implementation (staff / multi-user) ────────────────────────────────

export class CloudTenantDataService implements TenantDataService {
  readonly isCloud = true as const;
  private tenantId: string;
  private client: DynamoDBDocumentClient;

  constructor(tenantId: string) {
    this.tenantId = tenantId;
    this.client = getTrackedDynamoDBClient();
  }

  async getAll<T = any>(collection: string): Promise<T[]> {
    try {
      const result = await this.client.send(
        new ScanCommand({
          TableName: TENANT_TABLE,
          FilterExpression: '#pk = :pk',
          ExpressionAttributeNames: { '#pk': 'tenantCollection' },
          ExpressionAttributeValues: { ':pk': pk(this.tenantId, collection) },
        })
      );
      return (result.Items || []) as T[];
    } catch (err) {
      console.error(`[TenantData] getAll(${collection}) failed:`, err);
      return [];
    }
  }

  async put<T extends { id: string }>(collection: string, item: T): Promise<T> {
    const record = {
      ...item,
      tenantCollection: pk(this.tenantId, collection),
      tenantId: this.tenantId,
      updatedAt: new Date().toISOString(),
    };
    await this.client.send(
      new PutCommand({ TableName: TENANT_TABLE, Item: record })
    );
    return item;
  }

  async remove(collection: string, id: string): Promise<void> {
    await this.client.send(
      new DeleteCommand({
        TableName: TENANT_TABLE,
        Key: { tenantCollection: pk(this.tenantId, collection), id },
      })
    );
  }
}

// ─── Local implementation (admin / single-user) ───────────────────────────────

class LocalTenantDataService implements TenantDataService {
  readonly isCloud = false as const;

  async getAll<T = any>(collection: string): Promise<T[]> {
    try {
      return await localDB.getAllItems<T>(collection);
    } catch (err) {
      console.error(`[LocalData] getAll(${collection}) failed:`, err);
      return [];
    }
  }

  async put<T extends { id: string }>(collection: string, item: T): Promise<T> {
    await localDB.putItem(collection, item);
    return item;
  }

  async remove(collection: string, id: string): Promise<void> {
    await localDB.deleteItem(collection, id);
  }
}

// ─── Public factory ───────────────────────────────────────────────────────────

export interface TenantDataService {
  getAll<T = any>(collection: string): Promise<T[]>;
  put<T extends { id: string }>(collection: string, item: T): Promise<T>;
  remove(collection: string, id: string): Promise<void>;
  /** Whether this instance talks to the cloud (staff) or local DB (admin) */
  isCloud: boolean;
}

/**
 * Returns the correct TenantDataService based on whether the logged-in
 * user is a staff member (has tenantId) or an admin (no tenantId).
 */
export function getTenantDataService(
  currentUser: { uid: string; tenantId?: string } | null,
  userProfile?: { tenantId?: string } | null
): TenantDataService {
  const tenantId = currentUser?.tenantId || userProfile?.tenantId;

  if (tenantId) {
    return new CloudTenantDataService(tenantId);
  }

  return new LocalTenantDataService();
}
